/**
 * CR042: Admin → Scan Configuration page
 * Configure POC scan parameters
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, Save, Settings, Target, Store, X, Gift, Eye, Scan } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { GlobalFallbackSourcesPanel } from "@/components/admin/GlobalFallbackSourcesPanel";

interface ScanConfig {
  id: string;
  name: string;
  domain: string;
  merchant_limit: number;
  max_pages_per_source: number;
  scan_concurrency: number;
  use_validation_step: boolean;
  confidence_threshold: number;
  active_prompt_pack_id: string | null;
  selected_merchant_ids: string[] | null;
  use_self_resolving_read: boolean;
  // New quality gating settings
  min_confidence_for_display: number;
  require_deal_value: boolean;
  require_reward_value: boolean;
  // Global fallback sources
  use_global_fallback_sources: boolean;
  // Merchant-level FALLBACK source type toggle
  include_fallback_type_sources: boolean;
}

interface PromptPack {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
}

interface MerchantOption {
  merchant_id: string;
  merchant_name: string;
}

export default function ScanConfiguration() {
  const { isAdmin } = useAuth();
  const [config, setConfig] = useState<ScanConfig | null>(null);
  const [promptPacks, setPromptPacks] = useState<PromptPack[]>([]);
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [merchantLimit, setMerchantLimit] = useState(30);
  const [maxPagesPerSource, setMaxPagesPerSource] = useState(8);
  const [scanConcurrency, setScanConcurrency] = useState(3);
  const [useValidationStep, setUseValidationStep] = useState(false);
  const [useSelfResolvingRead, setUseSelfResolvingRead] = useState(false);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.6);
  const [activePromptPackId, setActivePromptPackId] = useState<string>("");
  const [selectedMerchantIds, setSelectedMerchantIds] = useState<string[]>([]);
  // New quality gating settings
  const [minConfidenceForDisplay, setMinConfidenceForDisplay] = useState(0.6);
  const [requireDealValue, setRequireDealValue] = useState(true);
  const [requireRewardValue, setRequireRewardValue] = useState(true);
  // Global fallback sources
  const [useGlobalFallbackSources, setUseGlobalFallbackSources] = useState(false);
  // Merchant-level FALLBACK source type toggle
  const [includeFallbackTypeSources, setIncludeFallbackTypeSources] = useState(false);
  
  // CR001: Spend & Earn toggles
  const [spendEarnScanningEnabled, setSpendEarnScanningEnabled] = useState(false);
  const [spendEarnUiEnabled, setSpendEarnUiEnabled] = useState(false);
  const [savingSpendEarn, setSavingSpendEarn] = useState(false);

  const fetchData = async () => {
    try {
      // Fetch scan config
      const { data: configData, error: configError } = await supabase
        .from("scan_configs")
        .select("*")
        .eq("domain", "gift_card_deals")
        .limit(1)
        .single();

      if (configError && configError.code !== "PGRST116") throw configError;

      // Fetch prompt packs (CR467: only SCAN packs for scan config)
      const { data: packsData, error: packsError } = await supabase
        .from("prompt_packs")
        .select("id, name, version, is_active")
        .eq("domain", "gift_card_deals")
        .eq("pack_kind", "SCAN")
        .is("deleted_at", null)
        .order("version", { ascending: false });

      if (packsError) throw packsError;

      // CR307: Fetch available merchants from Merchant Directory (Active only)
      const { data: merchantData, error: merchantError } = await supabase
        .from("v_merchants_directory")
        .select("merchant_key, display_name")
        .eq("directory_status", "active")
        .order("display_name");

      if (merchantError) throw merchantError;

      // Map to expected format for compatibility
      const mappedMerchants = (merchantData || []).map(m => ({
        merchant_id: m.merchant_key,
        merchant_name: m.display_name
      }));

      setMerchants(mappedMerchants);
      setPromptPacks(packsData || []);

      if (configData) {
        setConfig(configData);
        setMerchantLimit(configData.merchant_limit);
        setMaxPagesPerSource(configData.max_pages_per_source);
        setScanConcurrency(configData.scan_concurrency);
        setUseValidationStep(configData.use_validation_step);
        setUseSelfResolvingRead(configData.use_self_resolving_read || false);
        setConfidenceThreshold(configData.confidence_threshold);
        setActivePromptPackId(configData.active_prompt_pack_id || "");
        setSelectedMerchantIds(configData.selected_merchant_ids || []);
        // New quality gating settings
        setMinConfidenceForDisplay(configData.min_confidence_for_display ?? 0.6);
        setRequireDealValue(configData.require_deal_value ?? true);
        setRequireRewardValue(configData.require_reward_value ?? true);
        // Global fallback sources
        setUseGlobalFallbackSources(configData.use_global_fallback_sources ?? false);
        // Merchant-level FALLBACK source type toggle
        setIncludeFallbackTypeSources(configData.include_fallback_type_sources ?? false);
      }
      
      // CR001: Fetch Spend & Earn feature flags
      const { data: flagsData } = await supabase
        .from("feature_flags")
        .select("flag_name, enabled")
        .in("flag_name", ["enable_spend_earn_scanning", "enable_spend_earn_ui"]);
      
      if (flagsData) {
        for (const flag of flagsData) {
          if (flag.flag_name === "enable_spend_earn_scanning") {
            setSpendEarnScanningEnabled(flag.enabled);
          }
          if (flag.flag_name === "enable_spend_earn_ui") {
            setSpendEarnUiEnabled(flag.enabled);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching scan config:", err);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const toggleMerchant = (merchantId: string) => {
    setSelectedMerchantIds(prev =>
      prev.includes(merchantId)
        ? prev.filter(id => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  const clearAllMerchants = () => {
    setSelectedMerchantIds([]);
  };

  // CR001: Toggle Spend & Earn feature flags
  const handleSpendEarnToggle = async (flagName: string, enabled: boolean) => {
    setSavingSpendEarn(true);
    try {
      const { error } = await supabase
        .from("feature_flags")
        .update({ enabled, updated_at: new Date().toISOString() })
        .eq("flag_name", flagName);
      
      if (error) throw error;
      
      if (flagName === "enable_spend_earn_scanning") {
        setSpendEarnScanningEnabled(enabled);
      } else if (flagName === "enable_spend_earn_ui") {
        setSpendEarnUiEnabled(enabled);
      }
      
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${flagName === 'enable_spend_earn_scanning' ? 'Spend & Earn scanning' : 'Spend & Earn UI'}`);
    } catch (err) {
      console.error("Error updating feature flag:", err);
      toast.error("Failed to update setting");
    } finally {
      setSavingSpendEarn(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        merchant_limit: merchantLimit,
        max_pages_per_source: maxPagesPerSource,
        scan_concurrency: scanConcurrency,
        use_validation_step: useValidationStep,
        use_self_resolving_read: useSelfResolvingRead,
        confidence_threshold: confidenceThreshold,
        active_prompt_pack_id: activePromptPackId || null,
        selected_merchant_ids: selectedMerchantIds.length > 0 ? selectedMerchantIds : null,
        // New quality gating settings
        min_confidence_for_display: minConfidenceForDisplay,
        require_deal_value: requireDealValue,
        require_reward_value: requireRewardValue,
        // Global fallback sources
        use_global_fallback_sources: useGlobalFallbackSources,
        // Merchant-level FALLBACK source type toggle
        include_fallback_type_sources: includeFallbackTypeSources,
      };

      if (config) {
        const { error } = await supabase
          .from("scan_configs")
          .update(data)
          .eq("id", config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("scan_configs").insert({
          ...data,
          name: "Gift Deals POC Default",
          domain: "gift_card_deals",
        });

        if (error) throw error;
      }

      toast.success("Configuration saved");
      fetchData();
    } catch (err) {
      console.error("Error saving config:", err);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  const selectedMerchantNames = selectedMerchantIds
    .map(id => merchants.find(m => m.merchant_id === id)?.merchant_name || id)
    .slice(0, 5);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Test Runs Scan Configuration</h1>
          <p className="text-muted-foreground">
            Configure parameters for Website Runs test scans
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* CR001: Offer Types Section */}
            <Card className="md:col-span-2 border-2 border-dashed border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-primary" />
                  Offer Types
                </CardTitle>
                <CardDescription>
                  Control which offer types are scanned and displayed
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Spend & Earn Scanning Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Scan className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <Label htmlFor="spend-earn-scanning" className="font-medium">
                          Enable Spend & Earn Scanning
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Scan for spend-to-earn promotions (gift card/cash/points rewards)
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="spend-earn-scanning"
                      checked={spendEarnScanningEnabled}
                      onCheckedChange={(checked) => handleSpendEarnToggle("enable_spend_earn_scanning", checked)}
                      disabled={savingSpendEarn}
                    />
                  </div>
                  
                  {/* Spend & Earn UI Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Eye className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <Label htmlFor="spend-earn-ui" className="font-medium">
                          Show Spend & Earn in UI
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Display Spend & Earn section to public users
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="spend-earn-ui"
                      checked={spendEarnUiEnabled}
                      onCheckedChange={(checked) => handleSpendEarnToggle("enable_spend_earn_ui", checked)}
                      disabled={savingSpendEarn}
                    />
                  </div>
                </div>
                
                {/* Status summary */}
                <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Scanning: </span>
                    <Badge variant={spendEarnScanningEnabled ? "default" : "secondary"}>
                      {spendEarnScanningEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <Separator orientation="vertical" className="h-4" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">Public UI: </span>
                    <Badge variant={spendEarnUiEnabled ? "default" : "secondary"}>
                      {spendEarnUiEnabled ? "Visible" : "Hidden"}
                    </Badge>
                  </div>
                  {spendEarnUiEnabled && (
                    <>
                      <Separator orientation="vertical" className="h-4" />
                      <a 
                        href="/spend-earn" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        View Spend & Earn Page →
                      </a>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Scan Parameters */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Scan Parameters
                </CardTitle>
                <CardDescription>
                  Control how many merchants and pages to scan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="merchant_limit">Merchant Limit</Label>
                    <span className="text-sm font-medium">{merchantLimit}</span>
                  </div>
                  <Slider
                    id="merchant_limit"
                    value={[merchantLimit]}
                    onValueChange={(v) => setMerchantLimit(v[0])}
                    min={1}
                    max={merchants.length || 100}
                    step={1}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Number of merchants to scan per run (default: 30)
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="max_pages">Max Pages per Source</Label>
                    <span className="text-sm font-medium">{maxPagesPerSource}</span>
                  </div>
                  <Slider
                    id="max_pages"
                    value={[maxPagesPerSource]}
                    onValueChange={(v) => setMaxPagesPerSource(v[0])}
                    min={1}
                    max={20}
                    step={1}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Maximum flyer pages to scan per merchant (default: 8)
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="concurrency">Scan Concurrency</Label>
                    <span className="text-sm font-medium">{scanConcurrency}</span>
                  </div>
                  <Slider
                    id="concurrency"
                    value={[scanConcurrency]}
                    onValueChange={(v) => setScanConcurrency(v[0])}
                    min={1}
                    max={10}
                    step={1}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Parallel merchant scans (default: 3)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quality Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Quality Settings
                </CardTitle>
                <CardDescription>
                  Configure confidence thresholds and validation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Min Confidence for Display */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="min-confidence-display">Min Confidence for Display</Label>
                    <span className="text-sm font-medium">
                      {(minConfidenceForDisplay * 100).toFixed(0)}%
                    </span>
                  </div>
                  <Slider
                    id="min-confidence-display"
                    value={[minConfidenceForDisplay]}
                    onValueChange={(v) => setMinConfidenceForDisplay(v[0])}
                    min={0.1}
                    max={1.0}
                    step={0.05}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deals below this AI confidence will be rejected (default: 60%)
                  </p>
                </div>

                {/* Legacy confidence threshold - keeping for backwards compat */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="confidence">Extraction Confidence Threshold</Label>
                    <span className="text-sm font-medium">
                      {(confidenceThreshold * 100).toFixed(0)}%
                    </span>
                  </div>
                  <Slider
                    id="confidence"
                    value={[confidenceThreshold]}
                    onValueChange={(v) => setConfidenceThreshold(v[0])}
                    min={0.1}
                    max={1.0}
                    step={0.05}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Minimum confidence for extraction step (default: 60%)
                  </p>
                </div>

                {/* Deal Value Requirements */}
                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                  <Label className="text-sm font-medium">Deal Value Requirements</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Control what constitutes a valid deal. Enable these to filter out headers/placeholders.
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="require-deal-value" className="text-sm">Require Deal Value</Label>
                      <p className="text-xs text-muted-foreground">
                        Must have buy_amount OR reward_value
                      </p>
                    </div>
                    <Switch
                      id="require-deal-value"
                      checked={requireDealValue}
                      onCheckedChange={setRequireDealValue}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="require-reward-value" className="text-sm">Require Reward Value</Label>
                      <p className="text-xs text-muted-foreground">
                        Must have explicit reward_value (%, $, points)
                      </p>
                    </div>
                    <Switch
                      id="require-reward-value"
                      checked={requireRewardValue}
                      onCheckedChange={setRequireRewardValue}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label htmlFor="validation">Use Validation Step</Label>
                    <p className="text-xs text-muted-foreground">
                      Run extracted deals through validation prompt
                    </p>
                  </div>
                  <Switch
                    id="validation"
                    checked={useValidationStep}
                    onCheckedChange={setUseValidationStep}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label htmlFor="self-resolving-read">Self-Resolving READ</Label>
                    <p className="text-xs text-muted-foreground">
                      Use Firecrawl actions to dismiss cookie modals and set location (experimental)
                    </p>
                  </div>
                  <Switch
                    id="self-resolving-read"
                    checked={useSelfResolvingRead}
                    onCheckedChange={setUseSelfResolvingRead}
                  />
                </div>

                <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    POC Recommendation
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Keep "Require Reward Value" ON to filter out header/placeholder text that looks like deals but has no actual value.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Merchant Selection */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  Merchant Selection
                  {selectedMerchantIds.length > 0 && (
                    <Badge variant="secondary">{selectedMerchantIds.length} selected</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Optionally select specific merchants to scan for troubleshooting. 
                  Leave empty to scan all active merchants.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selected merchants display */}
                {selectedMerchantIds.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Selected merchants:</Label>
                      <Button variant="ghost" size="sm" onClick={clearAllMerchants}>
                        <X className="h-4 w-4 mr-1" />
                        Clear all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedMerchantIds.map(id => {
                        const merchant = merchants.find(m => m.merchant_id === id);
                        return (
                          <Badge
                            key={id}
                            variant="outline"
                            className="cursor-pointer hover:bg-destructive/10"
                            onClick={() => toggleMerchant(id)}
                          >
                            {merchant?.merchant_name || id}
                            <X className="h-3 w-3 ml-1" />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Merchant list */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Available merchants ({merchants.length}):</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Select all merchants up to the merchant limit
                        const merchantsToSelect = merchants.slice(0, merchantLimit).map(m => m.merchant_id);
                        setSelectedMerchantIds(merchantsToSelect);
                      }}
                      disabled={selectedMerchantIds.length === Math.min(merchants.length, merchantLimit)}
                    >
                      Select All ({Math.min(merchants.length, merchantLimit)})
                    </Button>
                  </div>
                  <ScrollArea className="h-[200px] border rounded-lg p-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {merchants.map(merchant => (
                        <div
                          key={merchant.merchant_id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={merchant.merchant_id}
                            checked={selectedMerchantIds.includes(merchant.merchant_id)}
                            onCheckedChange={() => toggleMerchant(merchant.merchant_id)}
                          />
                          <label
                            htmlFor={merchant.merchant_id}
                            className="text-sm cursor-pointer truncate"
                          >
                            {merchant.merchant_name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {selectedMerchantIds.length === 0 && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>All merchants mode:</strong> The scan will use all active merchants up to the limit ({merchantLimit}).
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Prompt Pack Selection */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Active Prompt Pack</CardTitle>
                <CardDescription>
                  Select which prompt pack to use for scanning
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={activePromptPackId} onValueChange={setActivePromptPackId}>
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select a prompt pack" />
                  </SelectTrigger>
                  <SelectContent>
                    {promptPacks.map((pack) => (
                      <SelectItem key={pack.id} value={pack.id}>
                        {pack.name} (v{pack.version})
                        {pack.is_active && " ★"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {promptPacks.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    No prompt packs found. Create one in the Prompt Packs page.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Global Fallback Sources */}
            <div className="md:col-span-2">
              <GlobalFallbackSourcesPanel
                useGlobalFallback={useGlobalFallbackSources}
                onToggleGlobalFallback={setUseGlobalFallbackSources}
              />
            </div>

            {/* Merchant-level FALLBACK Source Type */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Merchant Fallback Sources
                </CardTitle>
                <CardDescription>
                  Control when sources with type "Fallback" are included in scans
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div>
                    <Label htmlFor="include-fallback-type" className="text-base font-medium">
                      Include Fallback Sources in Normal Scans
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      When enabled, sources with type "Fallback" are included in the normal source set (but still lower priority). 
                      When disabled, fallback sources only run as a last resort after primary sources fail.
                    </p>
                  </div>
                  <Switch
                    id="include-fallback-type"
                    checked={includeFallbackTypeSources}
                    onCheckedChange={setIncludeFallbackTypeSources}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Configuration
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
